import {
	Activity,
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react"
import {
	createPath,
	UNSAFE_DataRouterContext as DataRouterContext,
	UNSAFE_DataRouterStateContext as DataRouterStateContext,
	UNSAFE_LocationContext as LocationContext,
	useBlocker,
	useLocation,
	useNavigate,
	useOutlet
} from "react-router-dom"
import { usePostHog } from "posthog-js/react"
import { posthogEnabled } from "~/components/providers/Posthog"
import { useIsMobile } from "~/hooks/useMobile"
import { confirmAction } from "~/lib/native-dialog"
import { LinkContextMenuProvider } from "~/components/Link"

export type WorkspaceTab = {
	id: string
	path: string
	title: string
	kind: "page" | "issue" | "document"
	pinned?: boolean
}

const WorkspaceContext = createContext<ReturnType<
	typeof useWorkspaceState
> | null>(null)
const WorkspaceTabContext = createContext<{
	id: string
	setDirty: (source: string, dirty: boolean) => void
} | null>(null)

export function useWorkspace() {
	const workspace = useContext(WorkspaceContext)
	return workspace
}

export function useWorkspaceTab() {
	return useContext(WorkspaceTabContext)
}

function useWorkspaceState({
	orgSlug,
	userId,
	homePath,
	page
}: {
	orgSlug: string
	userId: string
	homePath: string
	page: Omit<WorkspaceTab, "id" | "path" | "pinned">
}) {
	const location = useLocation()
	const locationContext = useContext(LocationContext)
	const routerState = useContext(DataRouterStateContext)
	const outlet = useOutlet()
	const navigate = useNavigate()
	const isMobile = useIsMobile()
	const posthog = usePostHog()
	const path = createPath(location)
	const storageKey = `orbit-workspace:v2:${userId}:${orgSlug}`
	const [dirty, setDirty] = useState<Record<string, Record<string, boolean>>>(
		{}
	)
	const [state, setState] = useState(() => {
		let tabs: WorkspaceTab[] = []
		try {
			const stored: unknown = JSON.parse(
				localStorage.getItem(storageKey) ??
					localStorage.getItem(`orbit-route-tabs:${orgSlug}`) ??
					"[]"
			)
			if (Array.isArray(stored)) {
				const ids = new Set<string>()
				for (const entry of stored) {
					if (
						!entry ||
						typeof entry !== "object" ||
						typeof entry.id !== "string" ||
						!entry.id ||
						ids.has(entry.id) ||
						typeof entry.path !== "string" ||
						!entry.path.startsWith(`/${orgSlug}/`) ||
						entry.path.includes("\\") ||
						typeof entry.title !== "string" ||
						!entry.title.trim() ||
						!["page", "issue", "document"].includes(entry.kind)
					)
						continue
					const url = new URL(entry.path, window.location.origin)
					if (
						url.origin !== window.location.origin ||
						!url.pathname.startsWith(`/${orgSlug}/`)
					)
						continue
					ids.add(entry.id)
					// Migrate the earlier entity shortcuts to independent instances.
					const legacy = entry.id.startsWith(`${entry.kind}:`)
					tabs.push({
						id: legacy ? crypto.randomUUID() : entry.id,
						path:
							legacy && entry.kind === "document"
								? `${url.pathname.slice(0, url.pathname.lastIndexOf("/") + 1)}${encodeURIComponent(entry.id.slice("document:".length))}`
								: createPath(url),
						title: entry.title,
						kind: entry.kind,
						pinned: entry.pinned === true
					})
				}
			}
		} catch {
			// The workspace also works without browser storage.
		}
		tabs.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned))
		const existing =
			tabs.find((tab) => tab.id === location.state?.workspaceTabId) ??
			tabs.find((tab) => tab.path === path)
		const activeId = existing?.id ?? crypto.randomUUID()
		if (!existing) tabs = [...tabs, { id: activeId, path, ...page }]
		else
			tabs = tabs.map((tab) =>
				tab.id === activeId ? { ...tab, path, ...page } : tab
			)
		return {
			tabs,
			activeId,
			locationKey: location.key,
			panes: [{ id: activeId, outlet, locationContext, routerState }]
		}
	})

	// Capture each route's element and router context at navigation time. Hidden
	// pages must never observe another tab's URL or params.
	if (state.locationKey !== location.key) {
		const requestedId = isMobile ? undefined : location.state?.workspaceTabId
		const activeId = state.tabs.some((tab) => tab.id === requestedId)
			? requestedId
			: state.activeId
		const pane = { id: activeId, outlet, locationContext, routerState }
		const previousPane = state.panes.find((entry) => entry.id === activeId)
		if (
			previousPane &&
			previousPane.locationContext.location.pathname !== location.pathname
		) {
			setDirty((current) => {
				const next = { ...current }
				delete next[activeId]
				return next
			})
		}
		setState({
			...state,
			activeId,
			locationKey: location.key,
			tabs: state.tabs.map((tab) =>
				tab.id === activeId ? { ...tab, path, ...page } : tab
			),
			panes: state.panes.some((entry) => entry.id === activeId)
				? state.panes.map((entry) => (entry.id === activeId ? pane : entry))
				: [...state.panes, pane]
		})
	}

	useEffect(() => {
		setState((current) => {
			const active = current.tabs.find((tab) => tab.id === current.activeId)
			if (!active || (active.title === page.title && active.kind === page.kind))
				return current
			return {
				...current,
				tabs: current.tabs.map((tab) =>
					tab.id === current.activeId ? { ...tab, ...page } : tab
				)
			}
		})
	}, [page.title, page.kind])

	useEffect(() => {
		try {
			localStorage.setItem(storageKey, JSON.stringify(state.tabs))
		} catch {
			/* In-memory tabs remain usable. */
		}
	}, [storageKey, state.tabs])

	// Browser history carries instance identity even when two tabs have one URL.
	useEffect(() => {
		if (isMobile || location.state?.workspaceTabId === state.activeId) return
		void navigate(path, {
			replace: true,
			state: { ...location.state, workspaceTabId: state.activeId }
		})
	}, [isMobile, location.state, navigate, path, state.activeId])

	const dirtyTabIds = Object.keys(dirty).filter((id) =>
		Object.values(dirty[id] ?? {}).some(Boolean)
	)
	const blocker = useBlocker(
		({ currentLocation, nextLocation, historyAction }) => {
			const stayingInOrg = nextLocation.pathname.startsWith(`/${orgSlug}/`)
			if (!stayingInOrg) return dirtyTabIds.length > 0
			const requestedId = isMobile
				? undefined
				: nextLocation.state?.workspaceTabId
			// A newly opened tab may not have rendered yet. Old history entries for
			// closed tabs instead navigate the current instance and must protect it.
			const targetId =
				requestedId &&
				(historyAction !== "POP" ||
					state.tabs.some((tab) => tab.id === requestedId))
					? requestedId
					: state.activeId
			const targetPane = state.panes.find((pane) => pane.id === targetId)
			return (
				dirtyTabIds.includes(targetId) &&
				(targetPane?.locationContext.location.pathname ??
					currentLocation.pathname) !== nextLocation.pathname
			)
		}
	)
	const prompted = useRef<object | null>(null)
	useEffect(() => {
		if (blocker.state !== "blocked" || prompted.current === blocker.location)
			return
		prompted.current = blocker.location
		void confirmAction({
			title: "Unsaved changes",
			message: blocker.location.pathname.startsWith(`/${orgSlug}/`)
				? isMobile
					? "Leave this page and discard its unsaved changes?"
					: "Replace this tab's page and discard its unsaved changes? Switching workspace tabs keeps your changes."
				: "Leave this workspace and discard unsaved changes in its open tabs?"
		}).then((confirmed) => {
			if (confirmed) blocker.proceed()
			else blocker.reset()
		})
	}, [blocker, orgSlug, isMobile])

	useEffect(() => {
		const beforeUnload = (event: BeforeUnloadEvent) => {
			if (!dirtyTabIds.length) return
			event.preventDefault()
			event.returnValue = ""
		}
		window.addEventListener("beforeunload", beforeUnload)
		return () => window.removeEventListener("beforeunload", beforeUnload)
	}, [dirtyTabIds.length])

	const markDirty = useCallback(
		(tabId: string, source: string, value: boolean) => {
			setDirty((current) =>
				current[tabId]?.[source] === value
					? current
					: { ...current, [tabId]: { ...current[tabId], [source]: value } }
			)
		},
		[]
	)

	const open = (target = homePath) => {
		if (isMobile) return
		const tab = {
			id: crypto.randomUUID(),
			path: target,
			title: "New tab",
			kind: "page" as const
		}
		setState((current) => ({ ...current, tabs: [...current.tabs, tab] }))
		void navigate(target, { state: { workspaceTabId: tab.id } })
		if (posthogEnabled) posthog?.capture("workspace_tab_opened")
	}
	const select = (id: string) => {
		if (isMobile) return
		const tab = state.tabs.find((entry) => entry.id === id)
		if (!tab || id === state.activeId) return
		void navigate(tab.path, { state: { workspaceTabId: id } })
		if (posthogEnabled)
			posthog?.capture("workspace_tab_switched", { kind: tab.kind })
	}
	const close = async (id: string, scope: "tab" | "others" | "all" = "tab") => {
		if (isMobile) return
		const closingIds = new Set(
			state.tabs
				.filter(
					(tab) =>
						!tab.pinned &&
						(scope === "all" ||
							(scope === "others" ? tab.id !== id : tab.id === id))
				)
				.map((tab) => tab.id)
		)
		if (!closingIds.size) return
		if (
			dirtyTabIds.some((tabId) => closingIds.has(tabId)) &&
			!(await confirmAction({
				title: closingIds.size === 1 ? "Close tab" : "Close tabs",
				message:
					closingIds.size === 1
						? "Close this tab and discard its unsaved changes?"
						: "Close these tabs and discard their unsaved changes?"
			}))
		)
			return
		const index = state.tabs.findIndex((tab) => tab.id === state.activeId)
		const remaining = state.tabs.filter((tab) => !closingIds.has(tab.id))
		if (!remaining.length)
			remaining.push({
				id: crypto.randomUUID(),
				path: homePath,
				title: "New tab",
				kind: "page"
			})
		setDirty((current) =>
			Object.fromEntries(
				Object.entries(current).filter(([tabId]) => !closingIds.has(tabId))
			)
		)
		setState((current) => ({
			...current,
			tabs: remaining,
			panes: current.panes.filter((pane) => !closingIds.has(pane.id))
		}))
		if (closingIds.has(state.activeId)) {
			const next =
				(scope === "others"
					? remaining.find((tab) => tab.id === id)
					: undefined) ?? remaining[Math.min(index, remaining.length - 1)]!
			void navigate(next.path, {
				replace: true,
				state: { workspaceTabId: next.id }
			})
		}
		if (posthogEnabled)
			posthog?.capture("workspace_tabs_closed", {
				scope,
				count: closingIds.size
			})
	}
	const togglePin = (id: string) => {
		if (isMobile) return
		const tab = state.tabs.find((entry) => entry.id === id)
		if (!tab) return
		setState((current) => {
			const tabs = current.tabs.map((entry) =>
				entry.id === id ? { ...entry, pinned: !entry.pinned } : entry
			)
			tabs.sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned))
			return { ...current, tabs }
		})
		if (posthogEnabled)
			posthog?.capture("workspace_tab_pinned", { pinned: !tab.pinned })
	}
	const reorder = (id: string, targetId: string, after = false) => {
		if (isMobile || id === targetId) return
		setState((current) => {
			const tab = current.tabs.find((entry) => entry.id === id)
			const target = current.tabs.find((entry) => entry.id === targetId)
			if (!tab || !target || !!tab.pinned !== !!target.pinned) return current
			const tabs = current.tabs.filter((entry) => entry.id !== id)
			const index = tabs.findIndex((entry) => entry.id === targetId)
			tabs.splice(index + Number(after), 0, tab)
			return { ...current, tabs }
		})
	}

	return {
		...state,
		dirtyTabIds,
		markDirty,
		open,
		select,
		close,
		togglePin,
		reorder,
		orgSlug,
		isMobile
	}
}

export function Workspace({
	children,
	...props
}: Parameters<typeof useWorkspaceState>[0] & { children: ReactNode }) {
	const workspace = useWorkspaceState(props)
	return (
		<WorkspaceContext.Provider value={workspace}>
			<LinkContextMenuProvider workspace={workspace}>
				<div
					className="contents"
					onAuxClick={(event) => {
						if (
							workspace.isMobile ||
							event.button !== 1 ||
							event.defaultPrevented ||
							!(event.target instanceof Element)
						)
							return
						const link = event.target.closest("a[href]")
						if (
							!link ||
							link.hasAttribute("download") ||
							link.getAttribute("target") === "_blank"
						)
							return
						const url = new URL(
							link.getAttribute("href")!,
							window.location.href
						)
						if (
							url.origin !== window.location.origin ||
							!url.pathname.startsWith(`/${props.orgSlug}/`)
						)
							return
						event.preventDefault()
						workspace.open(createPath(url))
					}}
				>
					{children}
				</div>
			</LinkContextMenuProvider>
		</WorkspaceContext.Provider>
	)
}

export function WorkspaceOutlet() {
	const workspace = useWorkspace()
	const outlet = useOutlet()
	if (!workspace) return outlet
	return workspace.panes.map((pane) => (
		<WorkspacePane
			key={pane.id}
			pane={pane}
			active={pane.id === workspace.activeId}
			markDirty={workspace.markDirty}
		/>
	))
}

function WorkspacePane({
	pane,
	active,
	markDirty
}: {
	pane: ReturnType<typeof useWorkspaceState>["panes"][number]
	active: boolean
	markDirty: ReturnType<typeof useWorkspaceState>["markDirty"]
}) {
	const { isMobile } = useWorkspace()!
	const routerContext = useContext(DataRouterContext)!
	const activeRef = useRef(active)
	const pendingNavigation = useRef<Parameters<
		typeof routerContext.router.navigate
	> | null>(null)
	useLayoutEffect(() => {
		activeRef.current = active
		if (!active || !pendingNavigation.current) return
		const [to, options] = pendingNavigation.current
		pendingNavigation.current = null
		void routerContext.router.navigate(to, options)
	}, [active, routerContext])
	const scopedRouter = useMemo(
		() => ({
			...routerContext,
			router: {
				...routerContext.router,
				navigate: ((
					...args: Parameters<typeof routerContext.router.navigate>
				) => {
					// An async save finishing in a hidden tab must not move the active tab.
					if (!activeRef.current) {
						pendingNavigation.current = args
						return Promise.resolve()
					}
					return routerContext.router.navigate(...args)
				}) as typeof routerContext.router.navigate
			}
		}),
		[routerContext]
	)
	const tabContext = useMemo(
		() => ({
			id: pane.id,
			setDirty: (source: string, dirty: boolean) =>
				markDirty(pane.id, source, dirty)
		}),
		[markDirty, pane.id]
	)
	return (
		<Activity mode={active ? "visible" : "hidden"}>
			<div
				className="h-full min-h-0 min-w-0"
				role={isMobile ? undefined : "tabpanel"}
				id={`workspace-panel-${pane.id}`}
				aria-labelledby={isMobile ? undefined : `workspace-tab-${pane.id}`}
			>
				<WorkspaceTabContext.Provider value={tabContext}>
					<DataRouterContext.Provider value={scopedRouter}>
						<DataRouterStateContext.Provider value={pane.routerState}>
							<LocationContext.Provider
								key={pane.locationContext.location.pathname}
								value={pane.locationContext}
							>
								{pane.outlet}
							</LocationContext.Provider>
						</DataRouterStateContext.Provider>
					</DataRouterContext.Provider>
				</WorkspaceTabContext.Provider>
			</div>
		</Activity>
	)
}
